import { SupportedLanguageCode } from '@procureai/schemas';
import { DateFormatPreset, formatDate, formatNumber, NumberFormatPreset, setupFormatPresets } from '@procureai/ui-mui';
import { zodI18next } from '@procureai/zod-i18next';
import { Locale } from 'date-fns';
import * as dateFnsLocales from 'date-fns/locale';
import * as i18next from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import HttpApi from 'i18next-http-backend';
import { initReactI18next } from 'react-i18next';
import { z } from 'zod';

import { languages, defaultLanguage, devLanguage } from '../constants';
import { I18nNamespace } from '../types';
import { appConfig } from '~/common/config';
import { languageIsoCode } from '~/util/string';

const currency = 'EUR';

const intlLocaleByLanguage = {
  en: 'en-GB',
  de: 'de-DE',
  fr: 'fr-FR',
  it: 'it-IT',
  es: 'es-ES',
  pt: 'pt-PT',
  pl: 'pl-PL',
  fi: 'fi-FI',
  ko: 'ko-KR',
  nl: 'nl-NL',
  ro: 'ro-RO',
  th: 'th-TH',
  zh: 'zh-CN',
} as const;

/** Indexed with a plain string by callers, hence the widened alias. */
export const localeFromLanguage: Record<string, string> = intlLocaleByLanguage;

const dateFnsLocaleByLanguage = {
  en: dateFnsLocales.enGB,
  de: dateFnsLocales.de,
  fr: dateFnsLocales.fr,
  it: dateFnsLocales.it,
  es: dateFnsLocales.es,
  pt: dateFnsLocales.ptBR,
  pl: dateFnsLocales.pl,
  fi: dateFnsLocales.fi,
  ko: dateFnsLocales.ko,
  nl: dateFnsLocales.nl,
  ro: dateFnsLocales.ro,
  th: dateFnsLocales.th,
  zh: dateFnsLocales.zhCN,
} as const;

/** Indexed with a plain string by callers, hence the widened alias. */
export const dateFnsLocaleFromLanguage: Record<string, Locale> = dateFnsLocaleByLanguage;

// Every supported language must appear in both maps above. Without an entry the
// lookups fall back — inconsistently, German in the theme and English here — so a
// user gets translated text with another country's date and number formatting.
// This fails to compile when a code is added to SUPPORTED_LANGUAGE_CODES upstream.
type UnmappedIntlLocale = Exclude<SupportedLanguageCode, keyof typeof intlLocaleByLanguage>;
type UnmappedDateFnsLocale = Exclude<SupportedLanguageCode, keyof typeof dateFnsLocaleByLanguage>;
const _everyLanguageHasAnIntlLocale: UnmappedIntlLocale extends never ? true : never = true;
const _everyLanguageHasADateFnsLocale: UnmappedDateFnsLocale extends never ? true : never = true;
void _everyLanguageHasAnIntlLocale;
void _everyLanguageHasADateFnsLocale;

i18next.use(LanguageDetector);
i18next.use(initReactI18next);
i18next.use(HttpApi);

i18next.init({
  supportedLngs: appConfig.isDev ? [devLanguage] : languages,
  ns: Object.values(I18nNamespace),
  backend: {
    loadPath: `/locales/{{lng}}/{{ns}}.json?v=${appConfig.version}`,
    requestOptions: {
      credentials: 'include',
    },
  },
  defaultNS: I18nNamespace.common,
  fallbackLng: appConfig.isDev ? devLanguage : defaultLanguage,
  debug: appConfig.i18n.debug,
  keySeparator: '.',
  returnNull: false,
  interpolation: {
    escapeValue: false, // not needed for react as it escapes by default
    format: function (value, format, lng) {
      if (format) {
        if (DateFormatPreset[format as DateFormatPreset]) {
          return value ? formatDate(format as DateFormatPreset, value instanceof Date ? value : new Date(value)) : '';
        } else if (NumberFormatPreset[format as NumberFormatPreset]) {
          return formatNumber(format as NumberFormatPreset, Number(value));
        } else if (format === 'list') {
          return new Intl.ListFormat(lng, {
            style: 'long',
            type: 'conjunction',
          }).format(value);
        }
      }
      return value;
    },
  },
});

const setLanguage = (lng: string) => {
  const locale = localeFromLanguage[languageIsoCode(lng)] || localeFromLanguage.en;
  setupFormatPresets(locale, currency);
};

export const i18n = i18next.default;

z.config(zodI18next({ t: i18n.t, ns: 'validation' }));

i18n.on('languageChanged', setLanguage);
